import { useRef } from "react";
import { RotateCcw, Smartphone, Sparkles, Type } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { inserirNoCursor } from "./api";

/** Etiqueta que diz se a mensagem é criada pela CLEO (só liga/desliga) ou fixa (editável). */
function Etiqueta({ ia }) {
  return ia ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold text-violet-600 dark:text-violet-300">
      <Sparkles className="h-3 w-3" /> Criado pela CLEO
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#25d366]/10 px-2 py-0.5 text-[10px] font-semibold text-[#0a7d5a] dark:text-[#4ade80]">
      <Type className="h-3 w-3" /> Texto fixo
    </span>
  );
}

/** Comportamento criado pela CLEO: só liga/desliga. */
export function ItemIA({ label, hint, ligado, onLigar }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border bg-background px-3 py-2.5">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{label}</span>
          <Etiqueta ia />
        </div>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch checked={!!ligado} onCheckedChange={onLigar} />
    </div>
  );
}

/** Número (ex.: horas até cumprimentar de novo). */
export function ItemNumero({ label, hint, valor, min, max, sufixo, onMudar }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border bg-background px-3 py-2.5">
      <div className="min-w-0">
        <span className="text-sm font-medium">{label}</span>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex items-center gap-2">
        <Input type="number" min={min} max={max} value={valor ?? ""} onChange={(e) => onMudar(parseInt(e.target.value, 10) || min || 0)}
          className="h-9 w-20 text-center font-semibold tabular-nums" />
        {sufixo && <span className="text-xs text-muted-foreground">{sufixo}</span>}
      </div>
    </div>
  );
}

/**
 * Mensagem fixa: liga/desliga + texto editável + variáveis que entram no
 * cursor + "Ver no celular". `extras` entra entre o cabeçalho e o texto
 * (ex.: atraso em segundos das etapas do pedido). `semChave` = mensagem que
 * não tem liga/desliga (aviso de pausa).
 */
export function ItemTexto({
  label, hint, ligado, onLigar, texto, onTexto, variaveis = [], padrao, onVerPrevia, extras, somenteLeitura, notaLeitura,
  semChave,
}) {
  const ref = useRef(null);
  const inserir = (v) => {
    const r = inserirNoCursor(ref.current, texto || "", v);
    onTexto(r.texto);
    setTimeout(() => { ref.current?.focus(); ref.current?.setSelectionRange(r.cursor, r.cursor); }, 0);
  };
  return (
    <div className={`rounded-xl border bg-background px-3 py-2.5 space-y-2 ${ligado ? "" : "opacity-60"}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{label}</span>
            <Etiqueta />
          </div>
          {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
        </div>
        {!semChave && <Switch checked={!!ligado} onCheckedChange={onLigar} />}
      </div>
      {ligado && (
        <>
          {extras}
          {somenteLeitura ? (
            <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">{notaLeitura}</p>
          ) : (
            <>
              <Textarea ref={ref} value={texto || ""} onChange={(e) => onTexto(e.target.value)} rows={3}
                className="resize-y text-sm leading-relaxed" />
              <div className="flex flex-wrap items-center gap-1.5">
                {variaveis.map((v) => (
                  <button key={v} type="button" onClick={() => inserir(`{${v}}`)}
                    className="rounded-full border border-[#25d366]/50 bg-[#25d366]/10 px-2 py-0.5 text-[11px] font-semibold text-[#0a7d5a] dark:text-[#4ade80] hover:bg-[#25d366]/20">
                    {`{${v}}`}
                  </button>
                ))}
                <span className="flex-1" />
                {padrao !== undefined && texto !== padrao && (
                  <button type="button" onClick={() => onTexto(padrao)}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted">
                    <RotateCcw className="h-3 w-3" /> Voltar ao padrão
                  </button>
                )}
              </div>
            </>
          )}
          <button type="button" onClick={onVerPrevia}
            className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold hover:bg-muted">
            <Smartphone className="h-3.5 w-3.5" /> Ver no celular
          </button>
        </>
      )}
    </div>
  );
}
